import _ from 'lodash'
import { store } from '../base/App'

const LGS = 'Logging Service'
const LGS_ID = _.snakeCase(LGS)
export const ACCOUNT_SUPER_USER = 'Account Super User'

export const getRoles = () => _.get(store.getState(), 'roles', {})
export const shouldAppUsePlatformId = app =>
  (app.name !== LGS && app.app_id !== LGS_ID) && (app.role === LGS || app.role === LGS_ID)

/*! NOTE: hasRole with unit tests needs to keep in sync with @pan/cloud-service-saml/lib/utils/rbacUtils.js */

/**
 * check RBAC role by specifiy the role by using the rbac roles object
 * role can be string like `Traps` or `Traps.App Admin` or `Traps.123.Instance Admin`
 * or use : instead of . like `Traps:Instance Admin.123` while the tenant id at the end
 * or function (roles) => true/false
 * or array of mixed strings or functions using OR relation
 * @param {string|function|Array} expected rbac role
 * @param {Object} roles rbac roles object
 * @return {boolean}
 */
export const hasRole = (expected, roles = getRoles()) => {
  const role = expected && Array.isArray(expected) && expected.length === 1 ? expected[0] : expected
  const isFn = _.isFunction(role)
  // note: _.isEmpty(() => true) === true !!!
  if ((!isFn && _.isEmpty(role)) || !roles) {
    return false
  }
  if (roles[ACCOUNT_SUPER_USER] === true) {
    return true
  }
  if (role === 'ANY') {
    return true
  }
  if (_.isString(role)) {
    // if we see role has app_superuser which is RBAC V2 role string replace that with App Admin
    let rolePath = role.includes('app_superuser') ? role.replace('app_superuser', 'App Admin') : role
    if (role.includes(':')) {
      const [app, name, tid] = role.split(':')
      const role_name = tid && name === 'app_superuser' ? 'Instance Admin' : name
      rolePath = tid ? `${app}.${tid}.${role_name}` : `${app}.${role_name || 'App Admin'}`
    }
    const [app] = rolePath.split('.', 2)
    // logged-in roles contain expected role app then it has privilege to pass
    if (_.has(roles, app) && _.has(roles, `${app}.App Admin`)) {
      return _.has(roles, `${app}.App Admin`)
    }
    // return Boolean(_.get(roles, expected))
    return rolePath.startsWith('ANY.') || _.has(roles, rolePath) // roles should only have key: true or key: {...}
  }
  const fn = expected => hasRole(expected, roles)
  if (Array.isArray(role)) {
    return role.some(fn) // OR relationship for each rule
  }
  if (isFn) {
    fn.rbacRoles = roles
    return role(roles, fn) === true // must return true for allow
  }
  return false // by default
}

export const hasAppAdmin = (appRole, roles = getRoles()) => hasRole(`${appRole}.App Admin`, roles)
export const hasAppInstance = (appRole, instance, roles = getRoles()) =>
  hasRole(instance === '*' ? appRole : [`${appRole}.App Admin`, `${appRole}.${instance}`], roles)

/**
 * filter entitled app list with tenants by rbac role
 * @param {Array<{role: string, tenants: Array<Object>?}>} entitledAppsList array of app with tenants
 * @param {object} roles rbac roles obj
 * @return {Array<{role: string, tenants: Array<Object>?}}
 */
export const filterAppEntitlementsByRole = (entitledAppsList = [], roles = getRoles()) => {
  return entitledAppsList.filter(app => hasRole(app.role, roles)).map(app => {
    if (_.isEmpty(app.tenants) || hasRole(`${app.role}.App Admin`, roles)) {
      return app // no filter needed for no tenants or account super admin or app admin
    }
    const usePlatformId = shouldAppUsePlatformId(app)
    return {
      ...app,
      tenants: app.tenants.filter(t => {
        const tid = usePlatformId ? (
          t.platform_id || _.get(t.associations, [LGS, 'tenant_id'], t.tenant_id)
        ) : t.tenant_id
        return _.has(roles, tid === '*' ? app.role : [app.role, tid])
      })
    }
  })
}

export const filterAppEntitlementsByNoAccessFlag = (entitledAppsList = []) => {
  return entitledAppsList.filter(app => !app.no_access).map(app => {
    if (_.isEmpty(app.tenants)) {
      return app
    }
    return {
      ...app,
      tenants: app.tenants.filter(t => !t.no_access)
    }
  })
}
